// ErrorLogger - Global error handler for catching and logging client-side errors
//
// This service automatically catches and logs:
// - Uncaught JavaScript errors (window.onerror)
// - Unhandled promise rejections
//
// All errors are logged as 'system_error' event type and console messages are preserved.
//
// Usage:
//   // Automatically initialized in CandidateChatContext and CoachChatContext
//
//   // To manually log an error:
//   import errorLogger from '../services/ErrorLogger'
//   errorLogger.logError('Something went wrong', { additionalData: 'value' })
//
class ErrorLogger {
  constructor() {
    this.logEventCallback = null
    this.conversationId = null
    this.isInitialized = false
  }

  // Initialize the error logger with the log event callback
  initialize(logEventCallback, conversationId = null) {
    if (this.isInitialized) {
      return
    }

    this.logEventCallback = logEventCallback
    this.conversationId = conversationId
    this.isInitialized = true

    // Set up global error handlers
    this._setupGlobalHandlers()
  }

  // Update conversation ID when it changes
  setConversationId(conversationId) {
    this.conversationId = conversationId
  }

  // Set up global error handlers
  _setupGlobalHandlers() {
    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      this._logError({
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.stack || event.error?.toString(),
        type: 'uncaught_error',
      })

      // Keep console messages
      console.error('Uncaught error:', event.error || event.message)

      // Don't prevent default error handling
      return false
    })

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this._logError({
        message: event.reason?.message || event.reason,
        error: event.reason?.stack || event.reason?.toString(),
        type: 'unhandled_rejection',
      })

      // Keep console messages
      console.error('Unhandled promise rejection:', event.reason)

      // Don't prevent default error handling
      return false
    })
  }

  // Log error to the server
  _logError(errorData) {
    if (!this.logEventCallback) {
      console.warn('ErrorLogger - logEventCallback not set, cannot log error')
      return
    }

    const errorInfo = {
      ...errorData,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    }

    try {
      this.logEventCallback('system_error', errorInfo, this.conversationId)
    } catch (err) {
      // If logging fails, just log to console
      console.error('ErrorLogger - Failed to log error:', err)
    }
  }

  // Manual error logging method
  logError(message, errorData = {}) {
    this._logError({
      message,
      ...errorData,
      type: 'manual_error',
    })

    // Keep console message
    console.error(message, errorData)
  }

  // Cleanup
  cleanup() {
    this.isInitialized = false
    this.logEventCallback = null
    this.conversationId = null
    // Note: We don't remove event listeners as they're bound to specific function references
  }
}

// Export singleton instance
const errorLogger = new ErrorLogger()
export default errorLogger
